import React, { useCallback, useRef, useState } from "react";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import { CancelIcon, PauseIcon, PlayIcon } from "../../../../assets/icon";
import { AudioWaveformVisualizer } from "../../whatsapp-audio-recorder/whatsapp-audio-recorder";
import {
  audioControlsStyle,
  audioDurationStyle,
  audioInfoStyle,
  audioWaveformStyle,
  cancelButtonStyle,
  containerStyle,
  playButtonStyle,
  previewContainerStyle,
  previewWaveHeight,
} from "./styles";
import { useAuthenticatedFileUrl } from "../../../../hooks/useAuthenticatedFileUrl";

interface VoiceRecordingPreviewProps {
  audioUrl: string;
  onDismiss: () => void;
}

const formatTime = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
  const mins = Math.floor(safeSeconds / 60);
  const secs = Math.floor(safeSeconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

export const VoiceRecordingPreview: React.FC<VoiceRecordingPreviewProps> = ({
  audioUrl,
  onDismiss,
}) => {
  const { url: resolvedAudioUrl } = useAuthenticatedFileUrl(audioUrl);
  const effectiveAudioUrl = resolvedAudioUrl ?? audioUrl;
  const theme = useTheme();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  const togglePlayback = useCallback(async () => {
    if (!audioRef.current) return;

    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error("Audio playback error:", error);
      setIsPlaying(false);
    }
  }, [isPlaying]);

  const handleAudioLoaded = useCallback(() => {
    if (
      audioRef.current &&
      Number.isFinite(audioRef.current.duration) &&
      audioRef.current.duration > 0
    ) {
      setAudioDuration(audioRef.current.duration);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime || 0);
    }
  }, []);

  const handleAudioEnded = useCallback(() => {
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  return (
    <Box sx={containerStyle(theme)}>
      <Box sx={previewContainerStyle}>
        <audio
          ref={audioRef}
          src={effectiveAudioUrl}
          onLoadedMetadata={handleAudioLoaded}
          onCanPlayThrough={handleAudioLoaded}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleAudioEnded}
          preload="metadata"
          playsInline
          style={{ display: "none" }}
        />

        <Box sx={audioControlsStyle(theme)}>
          <IconButton
            onClick={() => {
              void togglePlayback();
            }}
            aria-label={isPlaying ? "Pause recording" : "Play recording"}
            sx={playButtonStyle}
          >
            {isPlaying ? (
              <PauseIcon width="11px" height="11px" />
            ) : (
              <PlayIcon width="11px" height="11px" />
            )}
          </IconButton>

          <Box sx={audioInfoStyle}>
            <Typography sx={audioDurationStyle(theme)}>
              {formatTime(
                Number.isFinite(currentTime) && currentTime >= 0
                  ? currentTime
                  : audioDuration,
              )}
            </Typography>
            <Box sx={audioWaveformStyle}>
              <AudioWaveformVisualizer
                isRecording={false}
                isPlaying={isPlaying}
                audioUrl={effectiveAudioUrl}
                audioRef={audioRef}
                waveHeight={previewWaveHeight}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <IconButton
        onClick={onDismiss}
        aria-label="Dismiss voice recording preview"
        sx={cancelButtonStyle}
      >
        <CancelIcon width="14px" height="14px" />
      </IconButton>
    </Box>
  );
};

export default VoiceRecordingPreview;
